import { useEffect, useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useData } from "@/context/DataContext";
import { Panel, ChartTip, Empty } from "@/components/Widgets";
import { errMsg, fmt, http } from "@/lib/api";
import { toast } from "sonner";

const VSelect = ({ value, onChange, options, testid }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className="w-[260px] border-slate-700 bg-slate-900/70" data-testid={testid}><SelectValue placeholder="Select version" /></SelectTrigger>
    <SelectContent className="bg-[#131c2e]">{options.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
  </Select>
);

export default function CompareVersions() {
  const { versions, data, horizon, filters } = useData();
  const fe = data.model.entities.find((x) => x.code === filters.entity);
  const inScope = (i) => !fe || i.sheet === fe.sheet || (i.sheet === "NCF" && i.label === fe.ncf_label);
  const opts = versions.filter((v) => v.status === "confirmed");
  const [a, setA] = useState(opts.find((v) => v.id !== data.version.id)?.id || opts[0]?.id);
  const [b, setB] = useState(data.version.id);
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (!a || !b) return;
    http.get("/compare", { params: { a, b } }).then((r) => setRes(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [a, b]);
  const chart = res ? res.years.map((y, i) => ({ y, a: res.series.closing.a[i], b: res.series.closing.b[i] })).slice(horizon.from, horizon.to + 1) : [];
  return (
    <div className="space-y-6" data-testid="compare-panel">
      <div className="flex flex-wrap items-center gap-3">
        <VSelect value={a} onChange={setA} options={opts} testid="compare-version-a-select" />
        <span className="text-xs text-slate-500">vs</span>
        <VSelect value={b} onChange={setB} options={opts} testid="compare-version-b-select" />
      </div>
      {opts.length < 2 && <Empty>Upload and confirm a second workbook version to compare.</Empty>}
      {res && (
        <>
          <Panel title="Closing funds" subtitle={`${res.a} vs ${res.b}`} testid="compare-chart">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chart}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="y" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} tickFormatter={fmt} width={60} />
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line dataKey="b" name={res.b} stroke="#F59E0B" strokeWidth={2.5} dot={false} />
                <Line dataKey="a" name={res.a} stroke="#94A3B8" strokeDasharray="5 4" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Entity totals (all common years)" testid="compare-entity-table">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="py-2">Entity</th>{["Revenue", "EBITDA", "NCF"].map((h) => <th key={h} className="py-2 text-right">{h} A → B</th>)}
                </tr></thead>
                <tbody>
                  {res.entities.filter((e) => !fe || e.code === fe.code).map((e) => (
                    <tr key={e.code} className="border-t border-slate-800">
                      <td className="py-2 text-slate-200">{e.short}</td>
                      {["income", "ebitda", "ncf"].map((k) => {
                        const d = (e[`${k}_b`] ?? 0) - (e[`${k}_a`] ?? 0);
                        return <td key={k} className="num py-2 text-right">{fmt(e[`${k}_a`])} → {fmt(e[`${k}_b`])} <span className={d >= 0 ? "text-emerald-400" : "text-rose-400"}>{Math.abs(d) > 0.05 ? `(${d > 0 ? "+" : ""}${d.toFixed(1)})` : ""}</span></td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
          <Panel title="Line item changes" subtitle={`${res.changed.length} changed · ${res.added.length} added · ${res.removed.length} removed`} testid="compare-changes">
            <div className="max-h-96 overflow-auto">
              <table className="w-full text-sm">
                <tbody>
                  {res.added.filter(inScope).map((i) => <ChangeRow key={`a-${i.key}`} i={i} tag="Added" cls="text-emerald-400" />)}
                  {res.removed.filter(inScope).map((i) => <ChangeRow key={`r-${i.key}`} i={i} tag="Removed" cls="text-rose-400" />)}
                  {res.changed.filter(inScope).map((i) => <ChangeRow key={`c-${i.key}`} i={i} tag={`${fmt(i.total_a)} → ${fmt(i.total_b)}`} cls="text-amber-400" />)}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

const ChangeRow = ({ i, tag, cls }) => (
  <tr className="border-t border-slate-800">
    <td className="py-2 pr-3 text-slate-400">{i.sheet}</td>
    <td className="py-2 pr-3 text-slate-500">{i.section || i.kind}</td>
    <td className="py-2 pr-3 text-slate-100">{i.label}</td>
    <td className={`num py-2 text-right ${cls}`}>{tag}</td>
  </tr>
);
